import { useEffect, useState, useCallback, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, PlayCircle } from "lucide-react";

const TRAINING_VIDEOS = [
  { id: "Z02wd2Dnd04", title: "Sesión técnica", tag: "Entreno" },
  { id: "FerwubIYzSI", title: "Trabajo de ritmo", tag: "Físico" },
  { id: "NpeWIQ2PydI", title: "Detrás del entreno", tag: "Cantera" },
  { id: "5mCNDiSx95M", title: "Tácticas y posesión", tag: "Pizarra" },
  { id: "tnQJr0vkW8Q", title: "Pretemporada", tag: "Bloque" },
];

export default function Differentiator() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    containScroll: "trimSnaps",
  });
  const [selected, setSelected] = useState(0);
  const lastInteractionRef = useRef(0);

  const scrollPrev = useCallback(() => {
    lastInteractionRef.current = Date.now();
    emblaApi && emblaApi.scrollPrev();
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    lastInteractionRef.current = Date.now();
    emblaApi && emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    const id = setInterval(() => {
      if (Date.now() - lastInteractionRef.current < 6000) return;
      emblaApi.scrollNext();
    }, 5500);
    return () => {
      emblaApi.off("select", onSelect);
      clearInterval(id);
    };
  }, [emblaApi]);

  return (
    <section
      data-testid="differentiator-section"
      className="relative py-32 md:py-40 border-y border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,184,0,0.06),transparent_60%)] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-8 flex items-center gap-3">
              <span className="h-px w-10 bg-[#FFB800]" />
              03 — Diferencial
            </p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold uppercase tracking-tight leading-[0.95]">
              Apostamos<br />
              por la <span className="text-[#FFB800]">profesionalidad.</span>
            </h2>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <p className="text-white/65 text-lg leading-relaxed">
              Trabajo serio, infraestructura cuidada y entrenamientos
              profesionales. Cada sesión está pensada para crecer como equipo
              y como personas.
            </p>
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={scrollPrev}
                aria-label="Anterior"
                data-testid="diff-video-prev"
                className="h-12 w-12 rounded-full grid place-items-center border border-white/10 hover:border-[#FFB800] hover:text-[#FFB800] transition-colors"
              >
                <ArrowLeft size={16} />
              </button>
              <button
                onClick={scrollNext}
                aria-label="Siguiente"
                data-testid="diff-video-next"
                className="h-12 w-12 rounded-full grid place-items-center border border-white/10 hover:border-[#FFB800] hover:text-[#FFB800] transition-colors"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Training videos carousel */}
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/45 mb-6 flex items-center gap-3">
          <span className="h-px w-8 bg-white/30" />
          Entrenamientos del club
        </p>
      </div>

      <div className="overflow-hidden pl-6 md:pl-10 lg:pl-16" ref={emblaRef} data-testid="diff-videos-embla">
        <div className="flex gap-5 md:gap-6">
          {TRAINING_VIDEOS.map((v, i) => (
            <a
              key={i}
              href={`https://www.youtube.com/watch?v=${v.id}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`training-video-${i}`}
              className="relative flex-shrink-0 w-[80%] md:w-[44%] lg:w-[32%] aspect-video rounded-[2rem] overflow-hidden bg-[#0A1121] group border border-white/5 hover:border-[#FFB800]/40 transition-colors"
            >
              <img
                src={`https://i.ytimg.com/vi_webp/${v.id}/sddefault.webp`}
                alt={v.title}
                className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent" />

              {/* Top tag */}
              <span className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-white bg-[#030712]/60 backdrop-blur rounded-full px-3 py-1.5 border border-white/15">
                {v.tag}
              </span>

              {/* Play */}
              <div className="absolute inset-0 grid place-items-center">
                <div className="h-16 w-16 rounded-full bg-[#FFB800]/0 group-hover:bg-[#FFB800] grid place-items-center transition-all duration-500">
                  <PlayCircle
                    size={50}
                    strokeWidth={1}
                    className="text-white/80 group-hover:text-[#030712] transition-colors duration-500"
                  />
                </div>
              </div>

              {/* Bottom info */}
              <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between gap-3">
                <p className="font-display font-bold uppercase tracking-tight text-white text-lg md:text-xl">
                  {v.title}
                </p>
                <span className="font-mono text-[10px] tracking-[0.25em] text-white/55">
                  0{i + 1}
                </span>
              </div>
            </a>
          ))}
          <div className="flex-shrink-0 w-6 md:w-10 lg:w-16" aria-hidden />
        </div>
      </div>

      {/* Dots */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 mt-10 flex items-center justify-between">
        <div className="flex gap-2">
          {TRAINING_VIDEOS.map((_, i) => (
            <span
              key={i}
              data-testid={`diff-video-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === selected ? "w-10 bg-[#FFB800]" : "w-1.5 bg-white/15"
              }`}
            />
          ))}
        </div>
        <p className="font-mono text-[11px] tracking-[0.3em] text-white/40">
          {String(selected + 1).padStart(2, "0")} / {String(TRAINING_VIDEOS.length).padStart(2, "0")}
        </p>
      </div>
    </section>
  );
}
